import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import EmployeeEditModal from './EmployeeEditModal.js';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { formatSSNForDisplay } from '../../../../utils/ssnUtils';
import PageHeader from '../../../layouts/PageHeader';
import Callout from '../../Common/Callout';
import StatusBadge from '../../Common/StatusBadge';
import moment from 'moment';
import 'moment/locale/sv';

import CompensationCard from './CompensationCard';
import AssignmentCard from './AssignmentCard';
import BankAccountCard from './BankAccountCard';
import TaxProfileCard from './TaxProfileCard';
import VacationBalanceCard from './VacationBalanceCard';
import EmployeePrivacyActions from './EmployeePrivacyActions';
import { Section, Facts, Fact, UpcomingBadge, formatDate } from './EmployeeDetailParts';

import { Edit3, Plus, RefreshCw, User as UserIcon } from 'lucide-react';

// Set Swedish locale globally for this component
moment.locale('sv');

const ROLE_LABELS = {
  regular: 'Vanlig anställd',
  company_director: 'Företagsledare',
  board_member: 'Styrelseledamot',
};

const ViewEmployee = ({ commercialReadOnly = false }) => {
  const { companyId, id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [currentContract, setCurrentContract] = useState(null);
  const [currentCompensation, setCurrentCompensation] = useState(null);
  const [compensationHistory, setCompensationHistory] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [bankAccountHistory, setBankAccountHistory] = useState([]);
  const [currentTaxProfile, setCurrentTaxProfile] = useState(null);
  const [taxProfileHistory, setTaxProfileHistory] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadEmployee = useCallback(async () => {
    const getEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${id}`);
    try {
      const result = await axios.get(getEmployeeApiUrl, { withCredentials: true });
      setEmployee(result.data.data || result.data);
      setError(null);
    } catch (error) {
      setError('Det gick inte att ladda anställd data. Försök igen senare.');
      console.error('Error loading employee:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId, id]);

  const loadCurrentContract = useCallback(async () => {
    try {
      const getEmployeeContractsApiUrl = getApiUrl(`${companyId}/hr/employees/${id}/contracts`);
      const result = await axios.get(getEmployeeContractsApiUrl, { withCredentials: true });

      const contractsArray = Array.isArray(result.data)
        ? result.data
        : (result.data?.data || result.data || []);

      const today = moment().startOf('day');

      // Find the active contract (started and not ended)
      let current = contractsArray.find(contract => {
        const validFrom = moment(contract.valid_from).startOf('day');
        const validTo = contract.valid_to ? moment(contract.valid_to).startOf('day') : null;
        return validFrom.isSameOrBefore(today, 'day') && (!validTo || validTo.isSameOrAfter(today, 'day'));
      });

      // If no active contract, check for upcoming/future contracts
      if (!current && contractsArray.length > 0) {
        // Find the next upcoming contract (closest future start date)
        const futureContracts = contractsArray
          .filter(contract => {
            const validFrom = moment(contract.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        if (futureContracts.length > 0) {
          current = { ...futureContracts[0], _isFuture: true };
        }
      }

      setCurrentContract(current || null);
    } catch (error) {
      console.error('Error loading contract:', error);
    }
  }, [companyId, id]);

  const loadCompensation = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/compensation/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/compensation/history`), { withCredentials: true })
      ]);

      let current = currentRes.data?.data || null;
      const history = historyRes.data?.data || [];

      console.log('Compensation - current from API:', current);
      console.log('Compensation - history from API:', history);

      // If no current compensation, check for future compensation
      if (!current && history.length > 0) {
        const today = moment().startOf('day');
        console.log('No current compensation, checking for future. Today:', today.format('YYYY-MM-DD'));

        const futureCompensation = history
          .filter(comp => {
            const validFrom = moment(comp.valid_from).startOf('day');
            const isAfterToday = validFrom.isAfter(today, 'day');
            console.log(`Checking comp valid_from: ${comp.valid_from}, isAfter today: ${isAfterToday}`);
            return isAfterToday;
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future compensation found:', futureCompensation);

        if (futureCompensation.length > 0) {
          current = { ...futureCompensation[0], _isFuture: true };
          console.log('Setting current compensation to future:', current);
        }
      }

      setCurrentCompensation(current);
      setCompensationHistory(history);
    } catch (error) {
      console.error('Error loading compensation:', error);
      // Don't set error - compensation might not exist yet
    }
  }, [companyId, id]);

  const loadAssignment = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/assignment/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/assignment/history`), { withCredentials: true })
      ]);

      setCurrentAssignment(currentRes.data?.data || null);
      setAssignmentHistory(historyRes.data?.data || []);
    } catch (error) {
      console.error('Error loading assignment:', error);
      // Don't surface — an employee without a contract has no assignment yet.
    }
  }, [companyId, id]);

  const loadBankAccount = useCallback(async () => {
    try {
      const [accountResult, historyResult] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/bank-account`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/bank-account/history`), { withCredentials: true })
      ]);

      let account = accountResult.data?.data || null;
      const history = historyResult.data?.data || [];

      console.log('Bank Account - current from API:', account);
      console.log('Bank Account - history from API:', history);

      // If no current bank account, check for future bank account
      if (!account && history.length > 0) {
        const today = moment().startOf('day');
        const futureBankAccounts = history
          .filter(acc => {
            const validFrom = moment(acc.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future bank accounts found:', futureBankAccounts);

        if (futureBankAccounts.length > 0) {
          account = { ...futureBankAccounts[0], _isFuture: true };
        }
      }

      setBankAccount(account);
      setBankAccountHistory(history);
    } catch (error) {
      console.error('Error loading bank account:', error);
      // Don't set error - bank account might not exist yet
    }
  }, [companyId, id]);

  const loadTaxProfile = useCallback(async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/tax-profile/current`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/hr/employees/${id}/tax-profile/history`), { withCredentials: true })
      ]);

      let current = currentRes.data?.data || null;
      const history = historyRes.data?.data || [];

      console.log('Tax Profile - current from API:', current);
      console.log('Tax Profile - history from API:', history);

      // If no current tax profile, check for future tax profile
      if (!current && history.length > 0) {
        const today = moment().startOf('day');
        const futureTaxProfiles = history
          .filter(profile => {
            const validFrom = moment(profile.valid_from).startOf('day');
            return validFrom.isAfter(today, 'day');
          })
          .sort((a, b) => moment(a.valid_from).diff(moment(b.valid_from)));

        console.log('Future tax profiles found:', futureTaxProfiles);

        if (futureTaxProfiles.length > 0) {
          current = { ...futureTaxProfiles[0], _isFuture: true };
        }
      }

      setCurrentTaxProfile(current);
      setTaxProfileHistory(history);
    } catch (error) {
      console.error('Error loading tax profile:', error);
      // Don't set error - tax profile might not exist yet
    }
  }, [companyId, id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadEmployee(),
      loadCurrentContract(),
      loadCompensation(),
      loadAssignment(),
      loadBankAccount(),
      loadTaxProfile()
    ]);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadEmployee(),
        loadCurrentContract(),
        loadCompensation(),
        loadAssignment(),
        loadBankAccount(),
        loadTaxProfile()
      ]);
    };
    loadData();
  }, [loadEmployee, loadCurrentContract, loadCompensation, loadAssignment, loadBankAccount, loadTaxProfile]);

  const handleShowEditModal = () => !commercialReadOnly && setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);
  const handleSaveChanges = (updatedEmployee) => {
    loadEmployee();
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Laddar anställd...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Callout tone="danger" role="alert">
        {error}
        <div className="mt-2">
          <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleRefresh}>
            <RefreshCw />
            Försök igen
          </button>
        </div>
      </Callout>
    );
  }

  const isActive = employee.status ? employee.status === 'active' : employee.active !== false;
  const employmentBadge = !currentContract
    ? <StatusBadge tone="warning">Inget kontrakt</StatusBadge>
    : currentContract._isFuture
      ? <StatusBadge tone="info">Påbörjar {formatDate(currentContract.valid_from)}</StatusBadge>
      : <StatusBadge tone="success">Aktiv anställning</StatusBadge>;

  const workingTime = currentContract?.working_hours
    ? `${currentContract.working_hours} %`
    : currentContract?.work_week_hours
      ? `${Math.round((currentContract.work_week_hours / 40) * 100)} %`
      : null;

  return (
    <>
      <PageHeader
        backTo={{ to: `/${companyId}/employees`, label: 'Tillbaka till personal' }}
        icon={UserIcon}
        title={`${employee.firstname} ${employee.lastname}`}
        subtitle={employee.jobtitle || 'Anställd'}
        actions={
          <>
            <EmployeePrivacyActions companyId={companyId} employeeId={id} onRedacted={loadEmployee} />
            <button
              type="button"
              className="ll-btn ll-btn--secondary"
              disabled={commercialReadOnly}
              onClick={handleShowEditModal}
              aria-label={`Redigera ${employee.firstname} ${employee.lastname}`}
            >
              <Edit3 />
              Redigera
            </button>
          </>
        }
      >
        <div className="emp-detail__meta">
          <span>{formatSSNForDisplay(employee.ssn) || '–'}</span>
          {employee.email && <a href={`mailto:${employee.email}`}>{employee.email}</a>}
          {employmentBadge}
          {!isActive && <StatusBadge tone="muted">Inaktiv</StatusBadge>}
        </div>
      </PageHeader>

      <div className="emp-detail">
        <div className="emp-detail__main">
          {currentContract ? (
            <>
              <Section
                id="employment-heading"
                title="Anställning"
                badge={<UpcomingBadge show={currentContract._isFuture} />}
                meta={currentContract._isFuture
                  ? `Påbörjar anställning ${formatDate(currentContract.valid_from)}`
                  : `Gäller från ${formatDate(currentContract.valid_from)}`}
                actions={
                  <Link to={`/${companyId}/employee/${id}/newcontract`} className="ll-btn ll-btn--ghost ll-btn--sm">
                    <Plus />
                    Nytt kontrakt
                  </Link>
                }
              >
                <Facts>
                  <Fact label="Startdatum">{formatDate(currentContract.valid_from)}</Fact>
                  <Fact label="Slutdatum">{currentContract.valid_to ? formatDate(currentContract.valid_to) : 'Tillsvidare'}</Fact>
                  <Fact label="Anställningstyp">{currentContract.employment_type || currentContract.employmenttype || 'Fast anställning'}</Fact>
                  <Fact label="Arbetstid">{workingTime}</Fact>
                  <Fact label="Semesterdagar">{currentContract.vacation_days || 25}</Fact>
                  <Fact label="Uppsägningstid">{currentContract.notice_period}</Fact>
                  {/* worker_category + high_risk_protection intentionally
                      hidden. Backend defaults (tjansteman, 0) apply to all
                      employees. Unhide here when a customer needs arbetare
                      routing or Försäkringskassan §13 exemption. */}
                </Facts>
              </Section>

              <CompensationCard
                employeeId={id}
                companyId={companyId}
                currentCompensation={currentCompensation}
                history={compensationHistory}
                currentContract={currentContract}
                onUpdate={loadCompensation}
              />

              <AssignmentCard
                employeeId={id}
                companyId={companyId}
                currentAssignment={currentAssignment}
                history={assignmentHistory}
                currentContract={currentContract}
                onUpdate={loadAssignment}
              />

              <VacationBalanceCard employeeId={id} companyId={companyId} />
            </>
          ) : (
            <section className="emp-detail__section emp-detail__needs-contract" aria-labelledby="needs-contract-heading">
              <h2 id="needs-contract-heading">Ytterligare uppgifter kräver ett kontrakt</h2>
              <p>
                Ersättning, placering, bankkonto och skatteuppgifter hör till anställningen.
                Skapa ett anställningskontrakt så låses de upp här.
              </p>
              <Link to={`/${companyId}/employee/${id}/newcontract`} className="ll-btn ll-btn--primary">
                <Plus />
                Skapa kontrakt
              </Link>
            </section>
          )}
        </div>

        <aside className="emp-detail__aside">
          <Section id="personal-info-heading" title="Personuppgifter">
            <Facts>
              <Fact label="Personnummer">{formatSSNForDisplay(employee.ssn)}</Fact>
              <Fact label="Telefon">
                {employee.phonenumber && <a href={`tel:${employee.phonenumber}`}>{employee.phonenumber}</a>}
              </Fact>
              <Fact label="E-post" wide>
                {employee.email && <a href={`mailto:${employee.email}`}>{employee.email}</a>}
              </Fact>
              <Fact label="Adress" wide>
                {(employee.address || employee.postnumber || employee.city) ? (
                  <>
                    {employee.address}
                    {employee.address && (employee.postnumber || employee.city) && <br />}
                    {[employee.postnumber, employee.city].filter(Boolean).join(' ')}
                  </>
                ) : null}
              </Fact>
              {employee.employee_role && employee.employee_role !== 'regular' && (
                <Fact label="Anställningsroll" wide>{ROLE_LABELS[employee.employee_role] || employee.employee_role}</Fact>
              )}
            </Facts>
          </Section>

          {currentContract && (
            <>
              <BankAccountCard
                employeeId={id}
                companyId={companyId}
                bankAccount={bankAccount}
                history={bankAccountHistory}
                currentContract={currentContract}
                onUpdate={loadBankAccount}
              />

              <TaxProfileCard
                employeeId={id}
                companyId={companyId}
                currentTaxProfile={currentTaxProfile}
                history={taxProfileHistory}
                currentContract={currentContract}
                onUpdate={loadTaxProfile}
              />
            </>
          )}
        </aside>
      </div>

      <EmployeeEditModal
        show={!commercialReadOnly && showEditModal}
        handleClose={handleCloseEditModal}
        employee={employee}
        companyId={companyId}
        onSave={handleSaveChanges}
      />
    </>
  );
};

export default ViewEmployee;
